import { TranslateService } from "@ngx-translate/core";
import { SiilaTranslationService } from "../core/services/siila-translation.service";
import { Injector } from "@angular/core";
import { LOCATION_INITIALIZED } from "@angular/common";
import { take } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { findLegacyRedirect } from "../core/config/legacy-redirects";
import { PrerenderReadyService } from "../shared/services/prerender-ready.service";
import {
  emitPrerenderRedirect,
  isPrerenderAgent
} from "../shared/utils/prerender";

export function appInitializerFactory(
  translateService: TranslateService,
  siilaTranslationService: SiilaTranslationService,
  injector: Injector
): () => Promise<unknown> {
  return () =>
    new Promise<unknown>((resolve: (value: unknown) => void) => {
      const locationInitialized = injector.get(
        LOCATION_INITIALIZED,
        Promise.resolve(null)
      );
      locationInitialized.then(() => {
        // Checked before anything reads the URL: a legacy address must not get
        // its language resolved or its route rendered. The promise is left
        // pending on purpose — the page is being replaced, so bootstrapping the
        // old URL would only flash its content first.
        if (followLegacyRedirect(siilaTranslationService, injector)) {
          return;
        }
        translateService.addLangs(["pt", "en", "es"]);
        translateService.setDefaultLang("en");
        const url = window.location.pathname;
        // What the URL asks for: the `/lang/<code>` suffix, or — on news-detail
        // routes — the slug WORD, which outranks the suffix (`/news/...` is the
        // English article, `/noticias/...` the pt/es one). Resolving it HERE, at
        // boot, is what makes the article render in the right language on the
        // first paint instead of flashing the deployment default and correcting
        // itself afterwards.
        const urlLang = siilaTranslationService.getLanguageFromPath(url);
        const resolvedLang =
          urlLang || siilaTranslationService.getDefaultLanguage();
        if (urlLang || !validateUrlLang(url)) {
          try {
            localStorage.setItem("currentLanguageHP", resolvedLang);
          } catch (error) {}
          translateService.setDefaultLang(resolvedLang);
        }

        const language = siilaTranslationService.getCurrentLanguage();
        // Some routes own their address bar and must see the URL as REQUESTED
        // to decide whether it earns a 301: `DetailNewsComponent` rewrites word
        // + slug + suffix once the article resolves, and `SiilaAcademyComponent`
        // classifies the `/lang/<segment>` it arrived on. Normalizing the
        // `/lang` suffix here would make an off-canonical URL look canonical and
        // silently swallow the redirect — which is exactly what happened to
        // `/academy/lang/fibra-analytics`. See `componentOwnsAddressBar`.
        siilaTranslationService
          .setLanguage(
            language,
            siilaTranslationService.componentOwnsAddressBar(url)
          )
          .pipe(take(1))
          .subscribe(
            () => resolve(null),
            err => console.error(err),
            () => resolve(null)
          );
      });
    });
}

/**
 * Send the visitor on if the requested URL is in `LEGACY_REDIRECTS`. Returns
 * true when a redirect was issued.
 *
 * Mirrors `DetailNewsComponent.canonicalizeURL`: the Prerender.io 301 pair is
 * emitted unconditionally (the renderer forwards the crawler's user agent, so it
 * cannot be told apart from a person), and the hard navigation is skipped only
 * when we can tell we are being prerendered, since it would discard the tags.
 * In that case the snapshot is the bare shell carrying the 301, signalled ready
 * right away instead of waiting out Prerender.io's own timeout.
 */
function followLegacyRedirect(
  siilaTranslationService: SiilaTranslationService,
  injector: Injector
): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const rule = findLegacyRedirect(
    environment.platformId,
    window.location.pathname
  );
  if (!rule) {
    return false;
  }
  const base = rule.toPlatform
    ? siilaTranslationService.getPlatformBaseUrl(rule.toPlatform)
    : window.location.origin;
  // Some dev/test environments do not declare the other deployments' URLs.
  if (!base) {
    return false;
  }
  const targetUrl = `${base.replace(/\/+$/, "")}${rule.to}${window.location
    .search || ""}`;

  emitPrerenderRedirect(document, encodeURI(targetUrl));
  if (isPrerenderAgent()) {
    const prerenderReady = injector.get(PrerenderReadyService, null);
    if (prerenderReady) {
      prerenderReady.signalReady();
    }
  } else {
    window.location.replace(targetUrl);
  }
  return true;
}

export function validateUrlLang(url) {
  if (
    url.indexOf("lang/en") >= 0 ||
    url.indexOf("lang/es") >= 0 ||
    url.indexOf("lang/pt-br") >= 0
  ) {
    return true;
  }
  return false;
}
